import type { EnergyDevice } from '../../energia/models/energy-device.model';

interface EnergyDevicesFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: {
      type: 'Point';
      coordinates: [number, number];
    };
    properties: {
      type: 'dispositivo_energia';
      id: string | null;
      name: string;
      device_type: EnergyDevice['device_type'];
      state: 'active' | 'inactive';
    };
  }>;
}

function hasValidCoordinates(
  coordinates: EnergyDevice['coordinates']
): coordinates is NonNullable<EnergyDevice['coordinates']> {
  if (!coordinates) {
    return false;
  }

  const { latitude, longitude } = coordinates;

  return (
    typeof latitude === 'number' &&
    Number.isFinite(latitude) &&
    typeof longitude === 'number' &&
    Number.isFinite(longitude)
  );
}

export type EnergyDeviceGisLayerKey =
  | 'dispositivos_energia'
  | 'analizadores_red';

export function getEnergyDeviceGisLayerKey(
  deviceType: EnergyDevice['device_type']
): EnergyDeviceGisLayerKey | null {
  if (deviceType === 'energy_counter') {
    return 'dispositivos_energia';
  }

  if (deviceType === 'network_analyzer') {
    return 'analizadores_red';
  }

  return null;
}

export function getEnergyDeviceSearchTypeLabel(
  deviceType: EnergyDevice['device_type']
): string | null {
  if (deviceType === 'energy_counter') {
    return 'Contador de energía';
  }

  if (deviceType === 'network_analyzer') {
    return 'Analizador de red';
  }

  return null;
}

export function buildEnergyDevicesFeatureCollection(
  devices: EnergyDevice[]
): EnergyDevicesFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: devices
      .filter(
        device =>
          getEnergyDeviceGisLayerKey(device.device_type) !== null &&
          typeof device._id === 'string' &&
          device._id.trim() !== '' &&
          hasValidCoordinates(device.coordinates)
      )
      .map(device => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            device.coordinates!.longitude,
            device.coordinates!.latitude,
          ],
        },
        properties: {
          type: 'dispositivo_energia',
          id: device._id,
          name: device.reference_name,
          device_type: device.device_type,
          state: device.is_connected ? 'active' : 'inactive',
        },
      })),
  };
}
